import React from 'react'
import PropTypes from 'prop-types'
import * as Styled from './NutritionTable.styles'

function NutritionTable({
  caption,
  captionColor,
  captionBackground,
  alignCaption,
  alignHeader,
  headerColor,
  headerBackground,
  alignCellData,
  headers,
  content,
  'data-test': dataTest,
  ...props
}) {
  return (
    <Styled.Table data-test={dataTest}>
      {caption && (
        <TableCaption
          caption={caption}
          alignCaption={alignCaption}
          captionColor={captionColor}
          captionBackground={captionBackground}
          dataTest={dataTest}
        />
      )}

      {headers && (
        <TableHeader
          headers={headers}
          dataTest={dataTest}
          alignHeader={alignHeader}
          headerColor={headerColor}
          headerBackground={headerBackground}
        />
      )}

      <TableBody content={content} dataTest={dataTest} {...props} />
    </Styled.Table>
  )
}

function TableCaption({ caption, alignCaption, dataTest, captionColor, captionBackground }) {
  return (
    <Styled.TableCaption
      data-test={`${dataTest}-table-caption`}
      alignCaption={alignCaption}
      captionColor={captionColor}
      captionBackground={captionBackground}
    >
      {caption}
    </Styled.TableCaption>
  )
}

function TableHeader({ headers, dataTest, alignHeader, headerColor, headerBackground }) {
  if (!headers) return null
  if (headers[0] !== 'Typical Values') {
    headers.unshift('Typical Values')
  }
  const tableHeaders = headers.map((header, key) => (
    <Styled.TableHeader key={key} alignHeader={alignHeader} headerColor={headerColor}>
      {header}
    </Styled.TableHeader>
  ))

  return (
    <thead>
      <Styled.TableRow data-test={`${dataTest}`} headerBackground={headerBackground}>
        {tableHeaders}
      </Styled.TableRow>
    </thead>
  )
}

function TableBody({ content = [], dataTest }) {
  if (!content.length) return null

  return (
    <tbody>
      <TableRows key={content.length} content={content} dataTest={dataTest} />
    </tbody>
  )
}

const nutritionIds = ['fat', 'saturates', 'sugars', 'salt']
const toColorCode = title => {
  for (let id = 0; id < nutritionIds.length; id += 1) {
    if (title.includes(nutritionIds[id])) return true
  }
  return false
}
const getColorCoding = (rowTitle, colTitle, value) => {
  if (!value || !rowTitle || !toColorCode(rowTitle.toLowerCase())) return ''
  if (colTitle.toLowerCase() === 'typical values' || colTitle.toLowerCase() === 'per 100g') return ''
  const numberValue = value && value.replace(/[^0-9.]/g, '')
  return numberValue <= 25 ? (numberValue <= 5 ? 'low' : 'medium') : 'high'
}

function TableRows({ content = [], dataTest }) {
  return content.map((row, key) => {
    return (
      <React.Fragment key={key}>
        <Styled.TableRow key={key} data-test={`${dataTest}-content-row`}>
          <Styled.TableHeader data-test={`${dataTest}-content-key`}>{row.nutrientName || '-'}</Styled.TableHeader>
          {row.servingData.map((value, cKey) => {
            return (
              <Styled.TableCell
                key={cKey}
                data-test={`${dataTest}-content-value`}
                alignCellData='center'
                colorCoding={getColorCoding(row.nutrientName, value.servingTitle, value.servingValue)}
              >
                {value.servingValue}
              </Styled.TableCell>
            )
          })}
        </Styled.TableRow>
        {row.ofWhich && (
          <Styled.TableRow key={key + content.length} data-test={`${dataTest}-content-row`}>
            <Styled.TableHeader data-test={`${dataTest}-content-key`} fontStyle='italic'>
              {row.ofWhich[0].nutrientName}
            </Styled.TableHeader>
            {row.ofWhich[0].servingData.map((value, owKey) => {
              return (
                <Styled.TableCell
                  key={owKey}
                  data-test={`${dataTest}-content-value`}
                  alignCellData='center'
                  colorCoding={getColorCoding(row.ofWhich[0].nutrientName, value.servingTitle, value.servingValue)}
                >
                  {value.servingValue}
                </Styled.TableCell>
              )
            })}
          </Styled.TableRow>
        )}
      </React.Fragment>
    )
  })
}

NutritionTable.propTypes = {
  /**
   * Specify an optional data-test attribute to be added to the table for e2e testing
   */
  'data-test': PropTypes.string,

  /**
   * Specify a caption for the table
   */
  caption: PropTypes.string,

  /**
   * Specify an optional Caption alignment
   */
  alignCaption: PropTypes.string,

  /**
   * Specify an optional color for the caption
   */
  captionColor: PropTypes.string,

  /**
   * Specify an optional background color for the caption
   */
  captionBackground: PropTypes.string,

  /**
   * Specify an optional Header cell <th> alignment
   */
  alignHeader: PropTypes.string,

  /**
   * Specify an optional color for header cells
   */
  headerColor: PropTypes.string,

  /**
   * Specify an optional background color for the header
   */
  headerBackground: PropTypes.string,

  /**
   * Specify an optional alignment for data <td> cell alignment
   */
  alignCellData: PropTypes.string,

  /**
   * Specify the table headers
   */
  headers: PropTypes.array,

  /**
   * Specify the table Content
   */
  content: PropTypes.array
}

NutritionTable.defaultProps = {
  caption: 'Table of nutritional information',
  alignCaption: 'center',
  captionColor: 'color.monochrome.darker',
  captionBackground: 'color.monochrome.lighter',
  alignHeader: 'center',
  headerColor: 'color.monochrome.dark',
  headerBackground: 'color_white',
  alignCellData: 'center',
  headers: [],
  content: [],
  'data-test': 'nutrition-info-table'
}

export { TableCaption, TableHeader, TableBody }
export default NutritionTable
